import { useRoutes } from 'react-router-dom';
import router from './routes';
import { ConfigProvider, App as AntdApp, theme } from 'antd';
import './App.css';
import { useEffect } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { SentryErrorBoundary } from './sentry';
import SessionMonitor from './components/SessionMonitor';
import IdleTimeoutMonitor from './components/IdleTimeoutMonitor';
import sessionConfig from './config/session';
import { appTitleSuffix } from './config/environment';
import { useAppSelector } from './store/hooks';
import { selectRiaProfile } from './store/features/userinfo/userinfoSlice';
import { persistSignupBrandContext } from './pages/signup/brandContext';
import {
  getClientPortalMenuActionColors,
  getClientPortalPrimaryActionColors,
  isValidCssColor,
} from './util/theme-color';

const DEFAULT_PRIMARY_COLOR = '#3A7BD5';

export const ThemedApp = () => {
  const { theme: currentTheme, applyDefaultTheme } = useTheme();
  const outlet = useRoutes(router);
  const riaProfile = useAppSelector(selectRiaProfile);
  const riaProfileData = riaProfile?.profile;
  const profileDefaultTheme = riaProfileData?.default_theme;

  // Resolve the firm's raw primary color from the authenticated profile's
  // landing_color, falling back to the Wavvest default. Action tokens below may
  // adjust this color so buttons and selected states remain visible on the
  // current surface.
  const rawLandingColor = riaProfileData?.landing_color?.trim();
  const landingColor =
    rawLandingColor && isValidCssColor(rawLandingColor) ? rawLandingColor : undefined;
  const primaryColor = landingColor || DEFAULT_PRIMARY_COLOR;
  const primaryActionColors = getClientPortalPrimaryActionColors({
    primaryColor,
    theme: currentTheme,
  });
  const menuActionColors = getClientPortalMenuActionColors({
    primaryColor,
    theme: currentTheme,
  });
  const primaryActionColor = primaryActionColors.backgroundColor;
  const primaryActionTextColor = primaryActionColors.foregroundColor;
  const menuActionColor = menuActionColors.backgroundColor;
  const menuActionTextColor = menuActionColors.foregroundColor;

  useEffect(() => {
    applyDefaultTheme(profileDefaultTheme);
  }, [applyDefaultTheme, profileDefaultTheme]);

  useEffect(() => {
    document.documentElement.style.setProperty('--brand-primary-color', primaryColor);
    document.documentElement.style.setProperty('--ant-color-primary', primaryActionColor);
    document.documentElement.style.setProperty('--ant-button-primary-color', primaryActionColor);
    document.documentElement.style.setProperty(
      '--primary-button-text-color',
      primaryActionTextColor,
    );
    document.documentElement.style.setProperty('--theme-color', primaryActionColor);
  }, [primaryColor, primaryActionColor, primaryActionTextColor]);

  // Mirror the authenticated firm's branding into the login-page localStorage
  // cache so visiting the sign-in page after logout (no /firm route) still
  // shows the firm's primary logo, brand color, and default theme. This
  // matches the advisor portal behavior. `persistSignupBrandContext` no-ops
  // the cache for non co-branded firms, falling back to the Wavvest logo.
  useEffect(() => {
    if (!riaProfileData) {
      return;
    }
    persistSignupBrandContext(riaProfileData);
  }, [riaProfileData]);

  return (
    <ConfigProvider
      theme={{
        algorithm: currentTheme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
        components: {
          Input: {
            controlHeight: 44,
            fontSize: 20,
            borderRadius: 5,
            colorBorder: currentTheme === 'dark' ? '#000000' : '#d9d9d9',
          },
          Button: {
            controlHeight: 40,
            fontSize: 16,
            borderRadius: 24,
            defaultColor: currentTheme === 'dark' ? '#ffffff' : '#000000',
            fontWeight: 600,
            colorPrimary: primaryActionColor,
            primaryColor: primaryActionTextColor,
          },
          Menu:
            currentTheme === 'dark'
              ? {
                  itemBg: '#7241894D',
                  itemSelectedBg: menuActionColor,
                  itemSelectedColor: menuActionTextColor,
                  itemHoverColor: '#000000',
                  itemHoverBg: '#EDEDED',
                  itemColor: '#EDEDED',
                  fontSize: 16,
                  itemBorderRadius: 100,
                  itemMarginInline: 0,
                  itemMarginBlock: 16,
                  colorSplit: '#fdfdfd00',
                }
              : {
                  itemBg: '#000000',
                  itemSelectedBg: menuActionColor,
                  itemSelectedColor: menuActionTextColor,
                  itemHoverColor: menuActionTextColor,
                  itemHoverBg: menuActionColor,
                  itemColor: '#EDEDED',
                  fontSize: 16,
                  itemBorderRadius: 100,
                  itemMarginInline: 0,
                  itemMarginBlock: 16,
                  colorSplit: '#fdfdfd00',
                },
          Radio: {
            colorText: '#000000',
            buttonBg: '#fff',
          },
          Modal: {
            titleFontSize: 24,
          },
        },
        token: { colorPrimary: primaryActionColor },
      }}
    >
      <AntdApp>
        <SessionMonitor>
          <IdleTimeoutMonitor
            idleTimeout={sessionConfig.idleTimeout}
            warningTime={sessionConfig.warningTime}
            enableMultiTabSync={sessionConfig.enableMultiTabSync}
          >
            {outlet}
          </IdleTimeoutMonitor>
        </SessionMonitor>
      </AntdApp>
    </ConfigProvider>
  );
};

// Error fallback component for Sentry ErrorBoundary
function ErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
      }}
    >
      <h1 style={{ marginBottom: '10px' }}>Something went wrong</h1>
      <p style={{ color: '#666', marginBottom: '20px' }}>{errorMessage}</p>
      <button
        onClick={resetError}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          cursor: 'pointer',
          backgroundColor: '#3A7BD5',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
        }}
      >
        Try Again
      </button>
    </div>
  );
}

function App() {
  useEffect(() => {
    document.title = 'Wavvest Client Portal' + appTitleSuffix;
  }, []);

  return (
    <SentryErrorBoundary
      fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}
      onError={(error) => {
        console.error('App Error Boundary caught:', error);
      }}
    >
      <ThemeProvider>
        <AuthProvider>
          <ThemedApp />
        </AuthProvider>
      </ThemeProvider>
    </SentryErrorBoundary>
  );
}

export default App;
